export const CODE_SESSION_TITLE_MAX_LENGTH = 80;

export function normalizeCodeSessionTitle(title: string): string {
  return title.trim().slice(0, CODE_SESSION_TITLE_MAX_LENGTH);
}

/**
 * A session's title before it is named: its first prompt, or for a prompt of files alone, the
 * first file's name and how many others came with it.
 */
export function codeSessionPromptTitle(
  prompt: string,
  attachments: readonly { readonly name: string }[],
): string {
  const [first, ...others] = attachments;
  if (prompt.length > 0 || first === undefined) {
    return prompt;
  }
  return others.length === 0 ? first.name : `${first.name} and ${others.length} more`;
}

const FORKED_TITLE = /^(.+) \(fork #(\d+)\)$/;

function forkNumber(title: string): number {
  const number = title.match(FORKED_TITLE)?.[2];
  return number === undefined ? 0 : Number.parseInt(number, 10);
}

/**
 * `Title (fork #N)`, counting up from a title that is already a fork and past
 * every earlier fork of the same parent (`siblingTitles`, archived ones
 * included), so siblings never share a number.
 * Adapted from OpenCode's `getForkedTitle` in
 * packages/opencode/src/session/session.ts (MIT, Copyright (c) 2025 opencode),
 * commit c10134729dd2ce00beb18604ec91f10319f59a78. The base title is shortened
 * instead of the suffix to stay within `CODE_SESSION_TITLE_MAX_LENGTH`.
 */
export function forkedCodeSessionTitle(title: string, siblingTitles: readonly string[]): string {
  const base = title.match(FORKED_TITLE)?.[1] ?? title;
  const next = Math.max(forkNumber(title), ...siblingTitles.map(forkNumber)) + 1;
  const suffix = ` (fork #${next})`;
  return `${base.slice(0, CODE_SESSION_TITLE_MAX_LENGTH - suffix.length).trimEnd()}${suffix}`;
}

/**
 * Rename commit rule shared with the header's inline rename. Adapted from T3
 * ChatHeader (MIT): trim, reject empty (the caller toasts), and skip the
 * mutation when nothing changed. Cap at `CODE_SESSION_TITLE_MAX_LENGTH`.
 */
export function resolveCodeSessionTitleCommit(input: {
  readonly title: string;
  readonly originalTitle: string;
}): { action: 'commit'; title: string } | { action: 'reject-empty' } | { action: 'noop' } {
  const title = normalizeCodeSessionTitle(input.title);
  if (title.length === 0) {
    return { action: 'reject-empty' };
  }
  if (title === input.originalTitle) {
    return { action: 'noop' };
  }
  return { action: 'commit', title };
}
